import BaseElement from '@/components/purchase/checkout/seat-selection/modal/canvas/BaseElement';
import PlaceIcon from '@/components/purchase/checkout/seat-selection/modal/canvas/PlaceIcon';
import PriceStrip from '@/components/purchase/checkout/seat-selection/modal/canvas/PriceStrip';
import TextLabel from '@/components/purchase/checkout/seat-selection/modal/canvas/TextLabel';
import { tenantSelector } from '@/features/configuration/configurationSelector';
import usePlaceElement from '@/hooks/usePlaceElement';
import type { FloorPlanIconsDTO, PlaceDTO } from '@/types/dto/agent';
import {
  getCalculatedStyleProperties,
  getTenantBrand,
  hslToHex,
  WHITE,
} from '@/utils/color';
import {
  getElementState,
  getElementTypeId,
  getOrientationConfig,
  getPassengerInitials,
  type PLACE_ICONS,
} from '@/utils/seatSelection';
import type { HSLString } from '@fleet/design-tokens';
import React from 'react';
import { Image } from 'react-konva';
import { useSelector } from 'react-redux';

interface PlaceElementProps {
  element: PlaceDTO;
  floorPlanIcons: FloorPlanIconsDTO;
  canvasHeight: number;
  xOffset?: number;
  yOffset?: number;
  selectedReservationId?: string;
  hoveredSeatId?: string;
  onClick?: () => void;
  allPassengers?: Array<{
    firstName: string;
    lastName: string;
    reservationId?: string;
    index: number;
  }>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const PlaceElement: React.FC<PlaceElementProps> = ({
  element,
  floorPlanIcons,
  canvasHeight,
  xOffset = 0,
  yOffset = 0,
  selectedReservationId,
  hoveredSeatId,
  onClick,
  allPassengers,
  onMouseEnter,
  onMouseLeave,
}) => {
  const tenant = useSelector(tenantSelector);
  const { brandColors } = getTenantBrand(tenant);
  const colors = getCalculatedStyleProperties(brandColors);
  const passengerInfo = allPassengers?.find(
    (passenger) => passenger.reservationId === element.reservationId
  );

  const typeId = getElementTypeId(element, floorPlanIcons);
  const elementData = {
    ...element,
    selectedReservationId,
    hoveredSeatId,
  };

  const text = getElementState<string | number | undefined>(elementData, {
    elementPartOfBookingAndSelected: element.number,
    elementPartOfBookingAndNotSelected: getPassengerInitials(passengerInfo),
    elementPartOfBookingAndNotSelectedAndHovered:
      getPassengerInitials(passengerInfo),
    elementAvailableWithWheelchair: undefined,
    elementAvailableWithWheelchairAndHovered: undefined,
    elementAvailable: element.number,
    elementAvailableAndHovered: element.number,
    elementBlocked: undefined,
    elementBooked: undefined,
  });

  const isInteractable = getElementState<boolean>(elementData, {
    elementPartOfBookingAndSelected: false,
    elementPartOfBookingAndNotSelected: true,
    elementPartOfBookingAndNotSelectedAndHovered: true,
    elementAvailableWithWheelchair: true,
    elementAvailableWithWheelchairAndHovered: true,
    elementAvailable: true,
    elementAvailableAndHovered: true,
    elementBlocked: false,
    elementBooked: false,
  });

  const iconKey = getElementState<keyof typeof PLACE_ICONS | undefined>(
    elementData,
    {
      elementPartOfBookingAndSelected: undefined,
      elementPartOfBookingAndNotSelected: undefined,
      elementPartOfBookingAndNotSelectedAndHovered: undefined,
      elementAvailableWithWheelchair: 'WHEELCHAIR',
      elementAvailableWithWheelchairAndHovered: 'WHEELCHAIR',
      elementAvailable: undefined,
      elementAvailableAndHovered: undefined,
      elementBlocked: 'BLOCKED',
      elementBooked: 'BOOKED',
    }
  );

  const price = getElementState<number | undefined>(elementData, {
    elementPartOfBookingAndSelected: undefined,
    elementPartOfBookingAndNotSelected: undefined,
    elementPartOfBookingAndNotSelectedAndHovered: undefined,
    elementAvailableWithWheelchair: element.selectionFee?.amount,
    elementAvailableWithWheelchairAndHovered: element.selectionFee?.amount,
    elementAvailable: element.selectionFee?.amount,
    elementAvailableAndHovered: element.selectionFee?.amount,
    elementBlocked: undefined,
    elementBooked: undefined,
  });

  const { backgroundColor, outlineColor, textColor } = getElementState<{
    backgroundColor: HSLString;
    outlineColor: HSLString;
    textColor: HSLString;
  }>(elementData, {
    elementPartOfBookingAndSelected: {
      backgroundColor: colors.cta,
      outlineColor: colors['primary-dark'],
      textColor: WHITE,
    },
    elementPartOfBookingAndNotSelected: {
      backgroundColor: colors.dark,
      outlineColor: colors['neutral-pale'],
      textColor: WHITE,
    },
    elementPartOfBookingAndNotSelectedAndHovered: {
      backgroundColor: colors['primary-dark'],
      outlineColor: colors['neutral-pale'],
      textColor: WHITE,
    },
    elementAvailableWithWheelchair: {
      backgroundColor: WHITE,
      outlineColor: colors.primary,
      textColor: colors.dark,
    },
    elementAvailableWithWheelchairAndHovered: {
      backgroundColor: colors['primary-pale'],
      outlineColor: colors['primary-medium'],
      textColor: colors.dark,
    },
    elementAvailable: {
      backgroundColor: WHITE,
      outlineColor: colors.primary,
      textColor: colors.dark,
    },
    elementAvailableAndHovered: {
      backgroundColor: colors['primary-pale'],
      outlineColor: colors['primary-medium'],
      textColor: colors.dark,
    },
    elementBlocked: {
      backgroundColor: colors['neutral-pale'],
      outlineColor: colors.neutral,
      textColor: colors.dark,
    },
    elementBooked: {
      backgroundColor: colors['neutral-pale'],
      outlineColor: colors.neutral,
      textColor: colors.dark,
    },
  });

  const { image } = usePlaceElement({
    typeId,
    backgroundColor,
    outlineColor,
    floorPlanIcons,
  });

  if (!image || !typeId) return null;

  const { width, height } = image;
  const orientationConfig = getOrientationConfig(
    typeId,
    element.orientation?.id
  );

  return (
    <BaseElement
      coordinates={element.coordinates}
      canvasHeight={canvasHeight}
      xOffset={xOffset}
      yOffset={yOffset}
      isInteractable={isInteractable}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Image
        image={image}
        width={width}
        height={height}
        offsetX={width / 2}
        offsetY={height / 2}
        x={0}
        y={0}
        rotation={orientationConfig.degrees}
      />
      {iconKey && (
        <PlaceIcon
          iconKey={iconKey}
          x={orientationConfig.elementOffset.x}
          y={orientationConfig.elementOffset.y}
          size={Math.min(width, height) * 0.5}
        />
      )}
      {text && (
        <TextLabel
          x={orientationConfig.elementOffset.x}
          y={orientationConfig.elementOffset.y}
          text={text}
          color={hslToHex(textColor)}
        />
      )}
      {price && (
        <PriceStrip
          price={price}
          width={width}
          xOffset={orientationConfig.priceStripOffset.x}
          yOffset={orientationConfig.priceStripOffset.y}
        />
      )}
    </BaseElement>
  );
};

export default PlaceElement;
